import { useEffect, useState } from 'react';
import Wordmark from '../components/Wordmark.jsx';
import {
  deleteAdminImage,
  fetchAdminImages,
  loginAdmin,
  updateAdminImage,
  uploadAdminImage
} from '../utils/api.js';

const passwordStorageKey = 'maldet-admin-password';

export default function Admin() {
  const [password, setPassword] = useState(() => localStorage.getItem(passwordStorageKey) || '');
  const [authenticated, setAuthenticated] = useState(() => Boolean(localStorage.getItem(passwordStorageKey)));
  const [images, setImages] = useState([]);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  async function loadImages(nextPassword = password) {
    setBusy(true);
    try {
      const nextImages = await fetchAdminImages(nextPassword);
      setImages(nextImages);
      setStatus('');
    } catch (error) {
      setStatus(error.message);
      setAuthenticated(false);
      localStorage.removeItem(passwordStorageKey);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (authenticated) loadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  async function handleLogin(event) {
    event.preventDefault();
    setBusy(true);
    try {
      await loginAdmin(password);
      localStorage.setItem(passwordStorageKey, password);
      setAuthenticated(true);
      setStatus('');
    } catch (error) {
      setStatus(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleUpload(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    if (!formData.get('image')?.name) return;

    setBusy(true);
    try {
      await uploadAdminImage(password, formData);
      form.reset();
      setStatus('Bild wurde hochgeladen und automatisch einsortiert.');
      await loadImages();
    } catch (error) {
      setStatus(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function patchImage(image, patch) {
    const optimistic = { ...image, ...patch };
    setImages((current) => current.map((item) => (item.id === image.id ? optimistic : item)));
    try {
      const payload = await updateAdminImage(password, image.id, patch);
      setImages((current) => current.map((item) => (item.id === image.id ? payload.image : item)));
    } catch (error) {
      setStatus(error.message);
      await loadImages();
    }
  }

  async function removeImage(image) {
    const confirmed = window.confirm(`Bild "${image.filename}" wirklich löschen?`);
    if (!confirmed) return;

    setBusy(true);
    try {
      await deleteAdminImage(password, image.id);
      setImages((current) => current.filter((item) => item.id !== image.id));
      setStatus('Bild wurde gelöscht.');
    } catch (error) {
      setStatus(error.message);
    } finally {
      setBusy(false);
    }
  }

  if (!authenticated) {
    return (
      <main className="admin-page admin-page--login">
        <div className="admin-login">
          <Wordmark />
          <p className="mono-label">Local archive access</p>
          <h1>Admin</h1>
          <form onSubmit={handleLogin}>
            <label>
              Passwort
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
              />
            </label>
            <button type="submit" disabled={busy}>
              Enter archive
            </button>
          </form>
          {status && <p className="admin-status">{status}</p>}
          <a href="/">Zur Experience</a>
        </div>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <header className="admin-header">
        <div>
          <Wordmark compact />
          <p className="mono-label">Image command room</p>
          <h1>Gallery Admin</h1>
        </div>
        <div className="admin-header__actions">
          <a href="/admin/proofing">Client Galleries</a>
          <a href="/">View site</a>
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem(passwordStorageKey);
              setAuthenticated(false);
            }}
          >
            Logout
          </button>
        </div>
      </header>

      <section className="admin-panel">
        <form className="upload-panel" onSubmit={handleUpload}>
          <label>
            Neues Bild
            <input type="file" name="image" accept="image/jpeg,image/png,image/webp" />
          </label>
          <label>
            Titel optional
            <input type="text" name="title" placeholder="Serie / Motiv / Stimmung" />
          </label>
          <button type="submit" disabled={busy}>
            Upload
          </button>
        </form>
        {status && <p className="admin-status">{status}</p>}
      </section>

      <section className="admin-grid" aria-busy={busy}>
        {images.map((image) => (
          <article className={image.active ? 'admin-card' : 'admin-card is-inactive'} key={image.id}>
            <img src={image.src} alt={image.title || image.filename} loading="lazy" />
            <div className="admin-card__body">
              <p>{image.filename}</p>
              <span>
                {image.orientation} / {image.width} x {image.height}
              </span>
              <label>
                Titel
                <input
                  type="text"
                  defaultValue={image.title}
                  onBlur={(event) => patchImage(image, { title: event.target.value })}
                />
              </label>
              <label>
                Order
                <input
                  type="number"
                  defaultValue={image.order}
                  onBlur={(event) => patchImage(image, { order: Number(event.target.value) })}
                />
              </label>
              <div className="admin-card__actions">
                <button type="button" onClick={() => patchImage(image, { active: !image.active })}>
                  {image.active ? 'Deactivate' : 'Activate'}
                </button>
                <button type="button" className="danger" onClick={() => removeImage(image)}>
                  Delete
                </button>
              </div>
            </div>
          </article>
        ))}
      </section>
    </main>
  );
}
